import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ramsService } from '../../services/ramsService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import RamsUploadPanel from './components/RamsUploadPanel';
import WorkerAssignPanel from './components/WorkerAssignPanel';
import AcknowledgementTable from './components/AcknowledgementTable';
import RamsDeleteConfirmModal from './components/RamsDeleteConfirmModal';
import { pdfExportService } from '../../services/pdfExportService';
import { toast } from 'sonner';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';
import { supabase } from '../../lib/supabase';

const RamsManagement = () => {
  const { user, userProfile } = useAuth();
  // NSherjFh PR 3: Delete + Restore + the "View deleted" toggle are
  // admin-only. Site managers retain upload/assign/PDF-export.
  const isAdmin = userProfile?.role === 'admin';
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const { t } = useLanguage();
  const [sites, setSites] = useState([]);
  const [company, setCompany] = useState(null);
  const [ramsDocs, setRamsDocs] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [siteWorkers, setSiteWorkers] = useState([]);
  const [selectedSiteForWorkers, setSelectedSiteForWorkers] = useState('');
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [exportingPdfId, setExportingPdfId] = useState(null);

  // NSherjFh PR 3: tab between active H&S docs (default) and the
  // soft-deleted recycle-bin view. Admin-only.
  const [viewDeleted, setViewDeleted] = useState(false);
  const [deletingRams, setDeletingRams] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Filters for acknowledgement table
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('');

  useEffect(() => {
    loadInitialData();
  }, [viewDeleted]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [companyResult, sitesResult] = await Promise.all([
        companyService?.getUserCompany(),
        siteService?.getSitesByUser(),
      ]);

      const comp = companyResult?.data;
      const siteList = sitesResult?.data || [];
      setCompany(comp);
      setSites(siteList);

      if (comp?.id) {
        await loadRamsData(comp?.id);
      }
    } catch (err) {
      setErrorMsg('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatErr = (err, fallback) => {
    if (err?.code === 'PGRST301' || err?.code === '42501') return `Permission denied: ${err?.message}`;
    return `${fallback}: ${err?.message || 'Unknown error'}`;
  };

  const loadRamsData = async (companyId) => {
    // NSherjFh PR 3: deleted-view skips the assignments fetch entirely —
    // the ack tracking table is hidden in that mode, and getAllAssignments
    // ForCompany already filters out soft-deleted parents (correctly), so
    // it'd come back empty regardless. Cheaper to skip.
    const [docsResult, assignmentsResult] = await Promise.all([
      ramsService?.getRamsForCompany(companyId, { includeDeleted: viewDeleted }),
      viewDeleted
        ? Promise.resolve({ data: [], error: null })
        : ramsService?.getAllAssignmentsForCompany(companyId),
    ]);
    if (docsResult?.error) {
      setErrorMsg(formatErr(docsResult?.error, 'Failed to load H&S documents'));
    } else if (assignmentsResult?.error) {
      setErrorMsg(formatErr(assignmentsResult?.error, 'Failed to load H&S document assignments'));
    }
    // includeDeleted: true returns BOTH active and deleted rows; narrow
    // to the soft-deleted ones for display in the Deleted tab.
    const allDocs = docsResult?.data || [];
    setRamsDocs(viewDeleted ? allDocs?.filter((d) => d?.deleted_at) : allDocs);
    setAllAssignments(assignmentsResult?.data || []);
  };

  // Load workers when a site is selected in the upload panel
  const handleSiteChangeForWorkers = async (siteId) => {
    setSelectedSiteForWorkers(siteId);
    if (!siteId) { setSiteWorkers([]); return; }
    const result = await ramsService?.getWorkersForSite(siteId);
    if (result?.error) {
      toast?.error(formatErr(result?.error, 'Failed to load workers for this site'));
    }
    setSiteWorkers(result?.data || []);
  };

  // When an admin picks a RAMS document in the assign panel, load the
  // workers for THAT document's site automatically. Removes the ordering
  // dependency on the upload panel and makes re-assigning an existing RAMS
  // a one-click flow.
  const handleRamsChangeForWorkers = async (ramsId) => {
    if (!ramsId) { setSiteWorkers([]); setSelectedSiteForWorkers(''); return; }
    const siteId = ramsDocs?.find((r) => r?.id === ramsId)?.site_id;
    if (!siteId) { setSiteWorkers([]); setSelectedSiteForWorkers(''); return; }
    setSelectedSiteForWorkers(siteId);
    const result = await ramsService?.getWorkersForSite(siteId);
    if (result?.error) {
      toast?.error(formatErr(result?.error, 'Failed to load workers for this site'));
    }
    setSiteWorkers(result?.data || []);
  };

  const handleUpload = async ({ title, siteId, file }) => {
    if (subscriptionReadOnly) return;
    if (!company?.id || !user?.id) return;
    // Submission-time site-status check — friendlier than the raw DB
    // trigger error. The form lets admins pick any site; if they pick a
    // completed one, bail before hitting Stripe / storage.
    if (siteId) {
      const { data: pickedSite } = await supabase
        ?.from('sites')
        ?.select('status')
        ?.eq('id', siteId)
        ?.maybeSingle();
      if (isSiteReadOnlyStatus(pickedSite?.status)) {
        toast?.error('This site is completed — please reactivate to continue.');
        return;
      }
    }
    setUploading(true);
    setErrorMsg('');
    const result = await ramsService?.uploadRams(title, siteId, file, user?.id, company?.id);
    setUploading(false);
    if (result?.error) {
      setErrorMsg(formatErr(result?.error, 'Upload failed'));
      return;
    }
    setSuccessMsg('H&S document uploaded successfully');
    setTimeout(() => setSuccessMsg(''), 4000);
    await loadRamsData(company?.id);
    await handleSiteChangeForWorkers(siteId);
  };

  const handleAssign = async (ramsId, workerIds) => {
    if (subscriptionReadOnly) return;
    setAssigning(true);
    setErrorMsg('');
    const result = await ramsService?.assignRamsToWorkers(ramsId, workerIds);
    setAssigning(false);
    if (result?.error) {
      setErrorMsg(formatErr(result?.error, 'Assignment failed'));
      return;
    }
    setSuccessMsg(`H&S document assigned to ${workerIds?.length} worker${workerIds?.length !== 1 ? 's' : ''}`);
    setTimeout(() => setSuccessMsg(''), 4000);
    if (company?.id) await loadRamsData(company?.id);
  };

  const handleExportRamsPdf = async (ramsDoc) => {
    try {
      setExportingPdfId(ramsDoc?.id);
      const docAssignments = allAssignments?.filter(a => a?.rams_id === ramsDoc?.id || a?.rams_document_id === ramsDoc?.id);
      const site = sites?.find(s => s?.id === ramsDoc?.site_id);
      await pdfExportService?.generateRamsAcknowledgement({
        ramsDoc,
        assignments: docAssignments,
        acknowledgements: docAssignments?.filter(a => a?.acknowledged),
        siteName: site?.site_name || '',
        companyName: company?.company_name || '',
      });
    } catch (err) {
      console.error('PDF export error:', err);
      toast?.error('Failed to export PDF. Please try again.');
    } finally {
      setExportingPdfId(null);
    }
  };

  // NSherjFh PR 3 — recoverable soft-delete handlers. Mirrors PR 2's
  // document-management flow: open modal, confirm, refresh.
  const handleDeleteClick = (ramsDoc) => {
    setDeletingRams(ramsDoc);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingRams?.id) {
      setIsDeleteModalOpen(false);
      return;
    }
    const target = deletingRams;
    try {
      const result = await ramsService?.deleteRamsDocument(target?.id);
      if (result?.error) throw result?.error;
      // Optimistic: drop from local list so the row disappears immediately.
      setRamsDocs((prev) => prev?.filter((d) => d?.id !== target?.id));
      setSuccessMsg(`"${target?.title}" moved to Deleted`);
      setTimeout(() => setSuccessMsg(''), 4000);
      // Refresh assignments so stats reflect the change.
      if (company?.id) await loadRamsData(company?.id);
    } catch (err) {
      console.error('[RamsManagement] delete failed:', err);
      setErrorMsg(formatErr(err, 'Failed to delete H&S document'));
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingRams(null);
    }
  };

  const handleRestore = async (ramsDoc) => {
    try {
      const result = await ramsService?.restoreRamsDocument(ramsDoc?.id);
      if (result?.error) throw result?.error;
      // Optimistic drop from the deleted list — switching back to the
      // active tab will pick the row up via the next loadRamsData.
      setRamsDocs((prev) => prev?.filter((d) => d?.id !== ramsDoc?.id));
      toast?.success(`"${ramsDoc?.title}" restored`);
    } catch (err) {
      console.error('[RamsManagement] restore failed:', err);
      toast?.error(formatErr(err, 'Failed to restore H&S document'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>H&S Docs - SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-6">

            <SubscriptionReadOnlyBanner className="mb-6" />

            {/* Page Header */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Icon name="FileCheck" size={20} className="text-accent" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {viewDeleted ? 'Deleted H&S documents' : t('ramsTitle')}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {viewDeleted
                      ? 'H&S documents removed from active views. Restore to bring them back.'
                      : t('ramsSubtitle')}
                  </p>
                </div>
              </div>
              {/* NSherjFh PR 3: tab between active and soft-deleted H&S docs.
                  Admin-only — uses the same role gate as the per-row Delete. */}
              {isAdmin && (
                <Button
                  variant={viewDeleted ? 'default' : 'outline'}
                  onClick={() => { setErrorMsg(''); setSuccessMsg(''); setViewDeleted((v) => !v); }}
                  iconName={viewDeleted ? 'FolderOpen' : 'Trash2'}
                  iconPosition="left"
                >
                  {viewDeleted ? 'View active' : 'View deleted'}
                </Button>
              )}
            </div>

            {/* Alerts */}
            {successMsg && (
              <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success rounded-lg px-4 py-3 mb-4 text-sm">
                <Icon name="CheckCircle" size={16} />
                <span>{successMsg}</span>
              </div>
            )}
            {errorMsg && (
              <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg px-4 py-3 mb-4 text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* NSherjFh PR 3: active view = full management workspace; deleted
                view = just a restorable list. Hide upload / assign / stats /
                ack table / PDF export when viewing the recycle bin. */}
            {!viewDeleted && (
              <>
                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: t('ramsTotalDocs'), value: ramsDocs?.length, icon: 'FileText', color: 'text-accent bg-accent/10' },
                    { label: t('ramsAssigned'), value: allAssignments?.length, icon: 'Users', color: 'text-blue-600 bg-blue-50' },
                    { label: t('ramsConfirmed'), value: allAssignments?.filter((a) => a?.acknowledged)?.length, icon: 'CheckCircle', color: 'text-success bg-success/10' },
                    { label: t('ramsPending'), value: allAssignments?.filter((a) => !a?.acknowledged)?.length, icon: 'Clock', color: 'text-destructive bg-destructive/10' },
                  ]?.map((stat) => (
                    <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat?.color}`}>
                          <Icon name={stat?.icon} size={18} />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                          <p className="text-xs text-muted-foreground">{stat?.label}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Upload + Assign Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <RamsUploadPanel
                    sites={sites}
                    onUpload={handleUpload}
                    uploading={uploading}
                    readOnly={subscriptionReadOnly}
                  />
                  <WorkerAssignPanel
                    rams={ramsDocs}
                    workers={siteWorkers}
                    onAssign={handleAssign}
                    onRamsChange={handleRamsChangeForWorkers}
                    assigning={assigning}
                    readOnly={subscriptionReadOnly}
                  />
                </div>

                {/* Acknowledgement Tracking Table */}
                <AcknowledgementTable
                  rows={allAssignments}
                  loading={false}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  filterStatus={filterStatus}
                  onFilterStatusChange={setFilterStatus}
                  filterSite={filterSite}
                  onFilterSiteChange={setFilterSite}
                  sites={sites}
                />

                {/* Export PDF + Delete per RAMS document */}
                {ramsDocs?.length > 0 && (
                  <div className="mt-6 bg-card border border-border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Download" size={16} className="text-accent" />
                      {t('ramsExportPdfSection')}
                    </h3>
                    <div className="space-y-2">
                      {ramsDocs?.map(ramsDoc => (
                        <div key={ramsDoc?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                          <span className="text-sm text-foreground">{ramsDoc?.title}</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleExportRamsPdf(ramsDoc)}
                              disabled={exportingPdfId === ramsDoc?.id}
                              className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 disabled:opacity-50 px-3 py-1.5 rounded-lg border border-accent/30 hover:bg-accent/5 transition-colors"
                            >
                              <Icon name="Download" size={13} />
                              {exportingPdfId === ramsDoc?.id ? t('exporting') : t('exportPdf')}
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => handleDeleteClick(ramsDoc)}
                                className="flex items-center gap-1.5 text-xs font-medium text-destructive hover:text-destructive/80 px-3 py-1.5 rounded-lg border border-destructive/30 hover:bg-destructive/5 transition-colors"
                                aria-label={`Delete ${ramsDoc?.title}`}
                              >
                                <Icon name="Trash2" size={13} />
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Deleted view: simple restorable list. Storage file is
                preserved; restore re-attaches it to all active surfaces. */}
            {viewDeleted && (
              <div className="bg-card border border-border rounded-xl p-4">
                {ramsDocs?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No deleted H&S documents.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {ramsDocs?.map((ramsDoc) => (
                      <div key={ramsDoc?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{ramsDoc?.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {ramsDoc?.site?.site_name || 'Site removed'}
                            {ramsDoc?.deleted_at ? ` · deleted ${new Date(ramsDoc?.deleted_at)?.toLocaleDateString('en-GB')}` : ''}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRestore(ramsDoc)}
                          className="flex items-center gap-1.5 text-xs font-medium text-accent hover:text-accent/80 px-3 py-1.5 rounded-lg border border-accent/30 hover:bg-accent/5 transition-colors"
                          aria-label={`Restore ${ramsDoc?.title}`}
                        >
                          <Icon name="RotateCcw" size={13} />
                          Restore
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
        <RamsDeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => { setIsDeleteModalOpen(false); setDeletingRams(null); }}
          onConfirm={handleConfirmDelete}
          ramsTitle={deletingRams?.title || ''}
        />
      </div>
    </>
  );
};

export default RamsManagement;
